// Redwood Conecta - Hybrid Module Release System
// Combines date-based releases with completion requirements

// SIMPLIFIED SEQUENTIAL MODULE SYSTEM - No dates, purely completion-based
export const MODULE_SEQUENCE = {
  'what-is-autism': {
    order: 1,
    requiresPrevious: null, // First module, always available
    title: { 
      en: 'What is Autism?', 
      es: '¿Qué es el Autismo?' 
    }
  },
  'early-signs': {
    order: 2,
    requiresPrevious: 'what-is-autism',
    title: { 
      en: 'Early Signs & Diagnosis', 
      es: 'Señales Tempranas y Diagnóstico' 
    }
  },
  'first-steps': {
    order: 3,
    requiresPrevious: 'early-signs',
    title: { 
      en: 'First Steps After Diagnosis', 
      es: 'Primeros Pasos Después del Diagnóstico' 
    }
  },
  'school-system': {
    order: 4,
    requiresPrevious: 'first-steps',
    title: { 
      en: 'Connecting and Navigating the School System', 
      es: 'Conectando y Navegando el Sistema Escolar' 
    }
  },
  'lanterman-act': {
    order: 5,
    requiresPrevious: 'school-system',
    title: { 
      en: 'The Lanterman Act and Regional Centers in California', 
      es: 'La Ley Lanterman y los Centros Regionales en California' 
    }
  },
  'therapy-strategies': {
    order: 6,
    requiresPrevious: 'lanterman-act',
    title: { 
      en: 'Therapy Options and Home Strategies', 
      es: 'Opciones de Terapia y Estrategias para el Hogar' 
    }
  }
};

// Module availability states
export const MODULE_STATUS = {
  AVAILABLE: 'available',        // Released + prerequisites met
  LOCKED: 'locked',             // Released but prerequisites not met
  COMING_SOON: 'coming_soon',   // Not yet released
  COMPLETED: 'completed'        // Available + user completed it
};

export class ModuleReleaseManager {
  constructor(moduleProgress = {}) {
    this.moduleProgress = moduleProgress;
    console.log('ModuleReleaseManager initialized (sequential mode)');
    console.log('Module progress:', moduleProgress);
  }

  // Check if prerequisites are completed
  arePrerequisitesMet(moduleId) {
    const moduleInfo = MODULE_SEQUENCE[moduleId];
    if (!moduleInfo || !moduleInfo.requiresPrevious) return true;
    
    const previousModuleId = moduleInfo.requiresPrevious;
    return this.isModuleCompleted(previousModuleId);
  }

  // Check if a module is completed (has quiz completion)
  isModuleCompleted(moduleId) {
    return this.moduleProgress[moduleId]?.quizCompleted || false;
  }

  // Get the status of a specific module (simplified - no dates)
  getModuleStatus(moduleId) {
    const prerequisitesMet = this.arePrerequisitesMet(moduleId);
    const isCompleted = this.isModuleCompleted(moduleId);

    if (isCompleted) {
      return MODULE_STATUS.COMPLETED;
    }

    if (prerequisitesMet) {
      return MODULE_STATUS.AVAILABLE;
    }

    return MODULE_STATUS.LOCKED;
  }

  // Get all modules with their statuses (simplified)
  getAllModulesWithStatus() {
    const modules = [];
    
    Object.keys(MODULE_SEQUENCE).forEach(moduleId => {
      const moduleInfo = MODULE_SEQUENCE[moduleId];
      const status = this.getModuleStatus(moduleId);
      console.log(`Module ${moduleId}: status=${status}, prerequisites=${this.arePrerequisitesMet(moduleId)}, completed=${this.isModuleCompleted(moduleId)}`);
      
      modules.push({
        id: moduleId,
        ...moduleInfo,
        status,
        isAccessible: status === MODULE_STATUS.AVAILABLE || status === MODULE_STATUS.COMPLETED
      });
    });

    console.log('All modules with status:', modules);
    return modules.sort((a, b) => a.order - b.order);
  }

  // Get next module to unlock
  getNextModuleToUnlock() {
    const modules = this.getAllModulesWithStatus();
    return modules.find(module => 
      module.status === MODULE_STATUS.LOCKED || 
      module.status === MODULE_STATUS.COMING_SOON
    );
  }

  // Get progress summary (simplified)
  getProgressSummary() {
    const modules = this.getAllModulesWithStatus();
    const available = modules.filter(m => m.status === MODULE_STATUS.AVAILABLE).length;
    const completed = modules.filter(m => m.status === MODULE_STATUS.COMPLETED).length;
    const total = modules.length;

    return {
      available,
      completed,
      total,
      percentageComplete: Math.round((completed / total) * 100),
      currentModule: Math.max(...modules
        .filter(m => m.status === MODULE_STATUS.AVAILABLE || m.status === MODULE_STATUS.COMPLETED)
        .map(m => m.order)
      )
    };
  }

  // Get next module to unlock (no dates)  
  getNextModuleToUnlock() {
    const modules = this.getAllModulesWithStatus();
    return modules.find(module => module.status === MODULE_STATUS.LOCKED);
  }
}

// Helper function to get status message for UI
export const getStatusMessage = (status, language = 'es') => {
  const messages = {
    [MODULE_STATUS.AVAILABLE]: {
      en: 'Available Now',
      es: 'Disponible Ahora'
    },
    [MODULE_STATUS.LOCKED]: {
      en: 'Complete Previous Module',
      es: 'Completa el Módulo Anterior'
    },
    [MODULE_STATUS.COMING_SOON]: {
      en: 'Coming Soon',
      es: 'Próximamente'
    },
    [MODULE_STATUS.COMPLETED]: {
      en: 'Completed',
      es: 'Completado'
    }
  };

  return messages[status]?.[language] || messages[MODULE_STATUS.COMING_SOON][language];
};

// Helper function to get status color for UI
export const getStatusColor = (status) => {
  const colors = {
    [MODULE_STATUS.AVAILABLE]: 'from-green-100 to-green-200 border-green-300',
    [MODULE_STATUS.LOCKED]: 'from-yellow-100 to-yellow-200 border-yellow-300',
    [MODULE_STATUS.COMING_SOON]: 'from-gray-100 to-gray-200 border-gray-300',
    [MODULE_STATUS.COMPLETED]: 'from-blue-100 to-blue-200 border-blue-300'
  };

  return colors[status] || colors[MODULE_STATUS.COMING_SOON];
};